import { authFetch } from '../../utils/authFetch';
import { defineStoredJson } from '../../utils/storage';

/**
 * "Anmelden als": an administrator signed in as somebody else. While it lasts,
 * the session token IS the other person's, and the administrator's own token
 * waits here to be put back.
 *
 * Kept through a wipe like the session token itself: losing it would leave the
 * administrator stuck as somebody else, with no way back but a fresh login.
 */
export interface Impersonation {
    /** The administrator's own session token, restored on the way back. */
    adminToken: string;
    /** Whom the banner names — the label the users grid showed. */
    targetName: string;
}

const storedImpersonation = defineStoredJson<Impersonation | null>({
    key: 'impersonation',
    keepOnWipe: true,
    fallback: () => null,
    decode: (parsed) => {
        const candidate = parsed as Partial<Impersonation> | null;
        return typeof candidate?.adminToken === 'string' &&
            typeof candidate.targetName === 'string'
            ? {
                  adminToken: candidate.adminToken,
                  targetName: candidate.targetName
              }
            : undefined;
    }
});

export function getImpersonation(): Impersonation | null {
    return storedImpersonation.read();
}

export function setImpersonation(impersonation: Impersonation | null): void {
    if (impersonation) {
        storedImpersonation.write(impersonation);
    } else {
        storedImpersonation.clear();
    }
}

/**
 * Ask the server for a session as `userId`. Administrators only; the server
 * refuses a disabled or role-less account, oneself, and an impersonation
 * started from inside another one.
 */
export async function requestImpersonationToken(
    userId: string
): Promise<string> {
    const res = await authFetch(`/api/users/${userId}/impersonate`, {
        method: 'POST'
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data.error || 'Impersonation failed');
    }
    return data.token as string;
}
